import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DialogLayer } from '../ui/Dialog';
import { trapTab } from '../ui/focus';
import { QUOTES_KEY } from './types';

/**
 * "Usuń wycenę…" from "Wyceny" (owner 2026-10-24: "usuwanie zbednych wycen"): the whole quote, with the rules of
 * "Usuń wersję" kept by the server. The list does not know every version, so the first try goes without `confirm`; when
 * the client may have a version the server names it (422 `version_sent`) and the delete is confirmed once more
 * ("Usuń mimo to", `confirm=true`); one with invoices is refused with the reason, shown in place.
 */
export function DeleteQuoteDialog({ quote, onClose }: { quote: { quoteId: string; quoteVersion: number; eventName: string }; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [warning, setWarning] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (confirm: boolean) => api(`/api/quotes/quotes/${quote.quoteId}?version=${quote.quoteVersion}${confirm ? '&confirm=true' : ''}`, { method: 'DELETE' }),
    onSuccess: () => onClose(),
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'version_sent') setWarning(e.message);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
  const error = remove.error instanceof ApiError ? remove.error : remove.error ? new ApiError(0, 'unknown', 'Nie udało się usunąć') : null;
  const refused = error && error.code !== 'version_sent' && error.status !== 409 ? error : null;
  return (
    <div className="dialog-backdrop">
      <div
        className="dialog panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-quote-title"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="delete-quote-title">Usunąć wycenę „{quote.eventName}”?</h2>
        {warning && <p className="q-delete-warning">{warning}</p>}
        <p>Zniknie cały kosztorys tego wydarzenia, ze wszystkimi wersjami. Wydarzenie i sprzętówka zostają; w historii zmian zostaje wpis. Jeśli chcesz tylko uporządkować listę, wybierz „Ukryj na liście”.</p>
        {error?.status === 409 && <ConflictNotice onReload={onClose} />}
        {refused && (
          <p className="alert" role="alert">
            {refused.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" autoFocus disabled={remove.isPending || refused !== null} onClick={() => remove.mutate(warning !== null)}>
            {remove.isPending ? 'Usuwanie…' : warning ? 'Usuń mimo to' : 'Usuń wycenę'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
